import { Component, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { RadioButtonModule } from 'primeng/radiobutton';
import { TagModule } from 'primeng/tag';
import { ProductService } from '../../../service/product.service';
import { CommonModule } from '@angular/common';
import { UserService } from '../../../../user/services/user.service';
import { ChannelService } from '../../../../import/service/channel.service';
import { ToastModule } from 'primeng/toast';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { SkeletonModule } from 'primeng/skeleton';
import { Shop } from '../../../../shop/types/shop.type';
import { TreeSelectModule } from 'primeng/treeselect';
import { Category } from '../../../../import/category.type';
import { FileUploadModule } from 'primeng/fileupload';
import { MessageService } from 'primeng/api';
import { ImageModule } from 'primeng/image';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { Channel } from '../../../model/channel.type';
import { Constants } from '../../../../../utils/Constants';
import { SelectButtonModule } from 'primeng/selectbutton';

@Component({
  selector: 'app-edit-attribute',
  standalone: true,
  imports: [
    CommonModule, FormsModule, TranslateModule, InputTextModule, ButtonModule,
    TagModule, RadioButtonModule, SelectModule, TextareaModule, ToastModule,
    ToggleSwitchModule, SkeletonModule, TreeSelectModule, FileUploadModule,
    ImageModule, ProgressSpinnerModule, SelectButtonModule
  ],
  providers: [MessageService],
  templateUrl: './edit-attribute.component.html',
  styleUrl: './edit-attribute.component.scss'
})
export class EditAttributeComponent implements OnInit {
  @ViewChild('fileUpload') fileUploadRef: any;

  values: any[];
  input: any = null;
  attribute: any;
  sellersProducts: any[];
  buttonOutlined = true;
  selectedValue = { attributeCode: '', value: null, codeValue: null, referenced: false, source: '' };
  masterProduct: any;
  checked = false;
  operatorValue: any;
  shop: Shop;
  categories: Category[];
  uploadedFiles: any[] = [];
  showSpinner = false;
  source: string;
  selectedChannel: Channel;
  showErrorMandatoryAttributes = false;
  selectedShop = Constants.EQUIPE_MP_SHOP_NAME;
  readonly shops = [
    {
      name: Constants.EQUIPE_MP_SHOP_LABEL,
      value: Constants.EQUIPE_MP_SHOP_NAME,
      codes: [
        { code: Constants.EQUIPE_MP_SHOP_CODE_FR, channel: 'FR' },
        { code: Constants.EQUIPE_MP_SHOP_CODE_BE, channel: 'BE' },
      ]
    },
    {
      name: Constants.PREACTIVATION_SHOP_LABEL,
      value: Constants.PREACTIVATION_SHOP_NAME,
      codes: [
        { code: Constants.PREACTIVATION_SHOP_CODE_FR, channel: 'FR' },
        { code: Constants.PREACTIVATION_SHOP_CODE_BE, channel: 'BE' },
      ]
    },
    {
      name: Constants.MASTER_CATALOGUE_SHOP_LABEL,
      value: Constants.MASTER_CATALOGUE_SHOP_NAME,
      codes: [
        { code: Constants.MASTER_CATALOGUE_SHOP_CODE_FR, channel: 'FR' },
        { code: Constants.MASTER_CATALOGUE_SHOP_CODE_BE, channel: 'BE' },
      ]
    },
  ];

  constructor(
    private readonly translate: TranslateService,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private readonly productService: ProductService,
    private readonly userService: UserService,
    private readonly channelService: ChannelService,
    private readonly messageService: MessageService,
  ) {}

  ngOnInit(): void {
    this.initializeComponent();
    this.fetchAttributeValue();
  }

  private initializeComponent(): void {
    this.shop = this.userService.getUser().defaultShop;
    this.attribute = this.config.data['attribute'];
    this.sellersProducts = this.config.data['ids'];
    this.masterProduct = this.config.data['masterProduct'];
    this.categories = this.config.data['categories'];
    this.selectedChannel = this.config.data['selectedChannel'];
  }

  private fetchAttributeValue(): void {
    const data = {
      productIds: this.config.data['ids'].map((id) => id.trim()),
      attributeCode: this.attribute.attributeCode,
      localized: this.attribute.localized,
      project: this.attribute.locale[0]
    };

    this.productService.getAttributeValue(data).subscribe({
      next: (res: any) => { 
        this.handleAttributeValueResponse(res)}
    });
  }

  private handleAttributeValueResponse(res: any[]): void {
    const isMasterOperator = this.hasRoleMasterOperator();
    if (isMasterOperator) {
      this.selectedShop = this.determineSelectedShop(res);
    }
    this.source = this.determineSource(res);
    this.checked = !isMasterOperator && !this.source.toLowerCase().includes(Constants.EQUIPE_MP_SHOP_NAME.toLowerCase());
    this.initializeValues(res);
  }

 
  private determineSelectedShop(res: any[]): string {
    const shopFromData = this.shops.find(shop =>
      res.some(val => val.source?.split('|')[0].trim().toLowerCase() === shop.value.toLowerCase())
    );
    return shopFromData?.value ?? this.selectedShop;
  }

  private determineSource(res: any[]): string {
    if (this.hasRoleMasterOperator()) {
      return this.getShopName(this.selectedShop);
    }

    const hasSource = (label: string) =>
      res.some(val => val.source?.split('|')[0].toLowerCase().includes(label.toLowerCase()));

    return hasSource(Constants.PREACTIVATION_SHOP_NAME) && !hasSource(Constants.EQUIPE_MP_SHOP_NAME)
      ? Constants.PREACTIVATION_SHOP
      : Constants.EQUIPE_MP_SHOP;
  }

  private getShopName(value: string): string {
    return this.shops.find(shop => shop.value === value)?.name ?? Constants.EQUIPE_MP_SHOP;
  }

  private getTargetShopName(): string {
    if (this.hasRoleMasterOperator()) {
      return this.selectedShop;
    }
    return this.checked ? Constants.PREACTIVATION_SHOP_NAME : Constants.EQUIPE_MP_SHOP_NAME;
  }

  private initializeValues(res: any[]): void {
    this.values = [];
    this.operatorValue = this.createOperatorValue();

    for (let attr of res) {
      if (attr.referenced) {
        this.selectedValue = attr;
      }
      if (attr.source.startsWith(this.shop.shopName)) {
        if(attr.attributeCode === 'Typology') {
          this.operatorValue = { ...attr, value : {categoryBreadcrumbLoc: attr.value}}
        }else {
          this.operatorValue = attr;
        }
        
      } else {
        this.values.push(attr);
      }
    }
  }

  private createOperatorValue(): any {
    return {
      attributeCode: this.attribute.attributeCode,
      value: null,
      codeValue: null,
      referenced: false,
      source: this.source,
    };
  }

  private validateEANInput(ean: string): boolean {
    // Validate format: one or more 13-digit numbers separated by a single space.
    const pattern = /^\d{13}(?: \d{13})*$/;
    if (!pattern.test(ean)) {
      return false;
    }
    // Validate duplicates: split and compare lengths.
    const eanList = ean.split(' ').filter(e => e.trim() !== '');
    const uniqueEANs = new Set(eanList);
    return eanList.length === uniqueEANs.size;
  }

  updateAttribute(): void {
    const attributeValue = this.extractAttributeValue();
    if (this.isMandatoryAttributeEmpty(attributeValue.value)) {
      this.showErrorMandatoryAttributes = true;
      return;
    }

    if (this.attribute.attributeCode === 'EANs/EAN') {
      if (!this.validateEANInput(attributeValue.value)) {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('alerts.errorSummary'),
          detail: 'Entrée EAN invalide. L\'entrée doit contenir un ou plusieurs nombres de 13 chiffres, séparés par un espace unique et sans doublons.'        });
        return;
      }
    }
    
    this.showErrorMandatoryAttributes = false;
    this.submitAttributeUpdate(attributeValue);
  }

  hasRoleMasterOperator(): boolean {
    return this.userService.hasRole([Constants.ROLE_MASTER_OPERATOR]);
  }
    
  private extractAttributeValue(): { value: string, code: string | null } {
    if (this.attribute.attributeType === 'LIST') {
      return {
        value: this.selectedValue['value']?.label ?? '',
        code: this.selectedValue['value']?.code ?? null
      };
    }
    
    if (this.isTypologyAttribute()) {
      if(this.buttonOutlined){
        this.selectedValue = this.operatorValue
      }
      
      return this.extractTypologyValue();
    }
    
    return {
      value: this.selectedValue?.['value'] ?? '',
      code: null
    };
  }
  
  private extractTypologyValue(): { value: string, code: string | null } {
    const selectedValue = this.selectedValue?.['value'];
    
    if (selectedValue == null) {
      return { value: '', code: null };
    }
    
    if (selectedValue.categoryBreadcrumbLoc !== undefined) {
      return {
        value: selectedValue.categoryBreadcrumbLoc ?? '',
        code: selectedValue.categoryId ?? null
      };
    }
    
    return {
      value: selectedValue ?? '',
      code: this.selectedValue?.[' codeValue'] ?? null
    };
  }
  
  private isTypologyAttribute(): boolean {
    return this.attribute.attributeCode?.toLowerCase() === 'typology';
  }
  
  private isMandatoryAttributeEmpty(value: string): boolean {
    const attributeCode = this.attribute.attributeCode;
    const isMandatoryAttribute = 
      this.isTypologyAttribute() || 
      attributeCode === 'SKU_PART' ||
      attributeCode === 'EANs/EAN' ||
      attributeCode?.startsWith("DisplayName");
      
    return isMandatoryAttribute && value === '';
  }

  private submitAttributeUpdate(attributeValue: { value: string, code: string | null }): void {
    const data = this.prepareUpdateData(attributeValue);
    this.showSpinner = true;
    this.productService.updateSingleAttribute(data).subscribe({
      next: () => this.handleUpdateSuccess(),
      error: () => this.handleUpdateError()
    });
  }

  private prepareUpdateData(attributeValue: { value: string, code: string | null }): any {
    return {
      productId: this.masterProduct.productId,
      shopCode: this.getShopCode(),
      shopName: this.getTargetShopName(),
      eans: this.attribute.attributeCode === 'EANs/EAN' ? [attributeValue.value] : this.masterProduct.ean,
      sku: this.attribute.attributeCode === 'SKU_PART' ? attributeValue.value : this.masterProduct.sku,
      user: this.userService.getUser().email,
      attributesModifications: [{
        localized: this.attribute.localized,
        project: this.attribute.locale,
        attributeCode: this.attribute.attributeCode,
        attributeValue: attributeValue.value,
        attributeCodeValue: attributeValue.code
      }],
      categoryBreadCrumb: this.isTypologyAttribute() ? attributeValue.value : this.masterProduct.category.categoryBreadCrumb
    };
  }

  private handleUpdateSuccess(): void {
    this.showSpinner = false;
    this.closeDialog('success');
  }

  private handleUpdateError(): void {
    this.showSpinner = false;
    this.closeDialog('error');
  }

  closeDialog(msg = null): void {
    this.ref.close(msg);
  }

  modelChange(): void {
    this.buttonOutlined = false;
  }

  switchChange(event: any): void {
    if (event.checked) {
      this.source = Constants.PREACTIVATION_SHOP;
      this.operatorValue.source = Constants.PREACTIVATION_SHOP;
    } else {
      this.source = Constants.EQUIPE_MP_SHOP;
      this.operatorValue.source = Constants.EQUIPE_MP_SHOP;

    }
  }

  shopChange(): void {
    this.source = this.getShopName(this.selectedShop);
    if (this.operatorValue) {
      this.operatorValue.source = this.source;
    }
  }

  onUpload(event: any): void {
    this.uploadFile(event);
  }

  onFileSelect(event: any): void {
  }

  uploadFile(event: any): void {
    this.uploadedFiles = event.files;
    this.fileUploadRef.clear();
    const data = this.prepareMediaUpdateData();
    const formData = new FormData();
    formData.append('image', this.uploadedFiles[0], this.uploadedFiles[0].name);
    formData.append('productDetails', new Blob([JSON.stringify(data)], {
      type: 'application/json'
    }));
    this.submitMediaUpdate(formData);
  }

  deleteMedia(): void {
    const data = this.prepareMediaUpdateData();
    const formData = new FormData();
    formData.append('image', null);
    formData.append('productDetails', new Blob([JSON.stringify(data)], {
      type: 'application/json'
    }));
    this.submitMediaUpdate(formData);
  }

  private prepareMediaUpdateData(): any {
    const value = this.selectedValue?.['value'] ?? '';
    return {
      productId: this.masterProduct.productId,
      shopCode: this.getShopCode(),
      shopName: this.getTargetShopName(),
      eans: this.masterProduct.ean,
      sku: this.masterProduct.sku,
      user: this.userService.getUser().email,
      attributesModifications: [{
        localized: this.attribute.localized,
        project: this.attribute.locale,
        attributeCode: this.attribute.attributeCode,
        attributeValue: value,
        attributeCodeValue: null
      }],
      categoryBreadCrumb: this.masterProduct.category.categoryBreadCrumb,
      isMedia: this.attribute.attributeType === 'MEDIA'
    };
  }

  private submitMediaUpdate(formData: FormData): void {
    this.showSpinner = true;
    this.productService.updateOperatorMediaProduct(formData).subscribe({
      next: () => this.handleUpdateSuccess(),
      error: () => this.handleUpdateError()
    });
  }

  getShopCode(): string {
    if (this.hasRoleMasterOperator()) {
      return this.shops
        .find(shop => shop.value === this.selectedShop)
        ?.codes.find(code => code.channel === this.selectedChannel.channelCode)?.code;
    }

    if (this.selectedChannel.channelCode === 'FR') {
      return this.checked ? Constants.PREACTIVATION_SHOP_CODE_FR : Constants.EQUIPE_MP_SHOP_CODE_FR;
    }
    return this.checked ? Constants.PREACTIVATION_SHOP_CODE_BE : Constants.EQUIPE_MP_SHOP_CODE_BE;
  }
}
